/**
 * Resgate de um convite contra o servidor.
 *
 * PORQUE E QUE ISTO NAO PODE SER FEITO LOCALMENTE — e a razao de este arquivo
 * existir:
 *
 * Um codigo de convite NAO e o identificador da familia. E um codigo curto, de
 * uso unico, que o servidor troca por uma linha de membro numa familia concreta.
 * Enquanto a entrada por convite foi tratada localmente, o codigo era usado como
 * `familyId` — e tudo o que ela escrevesse a seguir ficava numa familia que nao
 * existe em lado nenhum, sem forma de reconciliar depois. Pior do que nao
 * funcionar: parecia funcionar.
 *
 * Isto obriga a ter rede, e e a unica coisa em todo o aplicativo que obriga. Nao
 * contraria o ADR-0002: continuar sem convite e sempre possivel — comeca-se uma
 * familia propria, offline, e convida-se depois. O que nao se pode e inventar
 * uma familia alheia sem falar com quem a guarda.
 *
 * Este arquivo vive na raiz de composicao (`wiring/`), que e o unico sitio onde
 * o nome do transporte pode aparecer (ADR-0005, e a excecao esta escrita no
 * teste de arquitectura). Nenhuma tela sabe como isto e feito.
 */
import { ALL_ROLES, type FamilyRole } from '@flos/shared';

/** Porque e que o convite nao foi aceite. Cada motivo tem uma frase propria. */
export type RedeemFailure =
  | 'sem-servidor'
  | 'sem-conta'
  | 'sem-rede'
  | 'codigo-invalido'
  | 'codigo-usado'
  | 'outra-conta'
  | 'servidor';

export const REDEEM_MESSAGES: Record<RedeemFailure, string> = {
  'sem-servidor':
    'Este aparelho ainda não sabe onde fica o servidor da família. Peça a quem instalou o aplicativo, ou comece a sua própria família agora.',
  'sem-conta':
    'Para entrar numa família preciso que entre com a sua conta primeiro.',
  'sem-rede':
    'Preciso de internet para confirmar o convite com a sua família. Pode começar sozinha agora e entrar quando tiver ligação.',
  'codigo-invalido':
    'Esse código não serve. Confirme as letras — são oito, e o traço não conta.',
  'codigo-usado': 'Esse convite já foi usado ou já passou o prazo. Peça um código novo.',
  'outra-conta': 'Esse convite foi enviado para outra conta Google.',
  servidor: 'O servidor não conseguiu responder agora. Tente daqui a pouco.',
};

export interface RedeemSuccess {
  ok: true;
  familyId: string;
  memberId: string;
  displayName: string;
  role: FamilyRole;
  familyName: string;
}

export interface RedeemRejected {
  ok: false;
  reason: RedeemFailure;
  /** Frase pronta a mostrar. A do servidor ganha à nossa, quando existe. */
  message: string;
}

export type RedeemResult = RedeemSuccess | RedeemRejected;

export interface RedeemInviteOptions {
  /** URL `/exec` da familia. Vazio = este aparelho ainda nao esta configurado. */
  url: string;
  /** Token da conta. `null` quando ninguem entrou. */
  getToken: () => Promise<string | null>;
  code: string;
  displayName?: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

/** Codigo tal como o servidor o quer: so letras e numeros, em maiusculas. */
export function normalizeInviteCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Oito caracteres. O alfabeto do servidor nao tem 0, O, 1, I nem L. */
export function isWellFormedInviteCode(raw: string): boolean {
  return normalizeInviteCode(raw).length === 8;
}

/** Codigos de erro do servidor para os nossos motivos. */
function failureFor(code: string): RedeemFailure {
  switch (code) {
    case 'NOT_FOUND':
    case 'BAD_REQUEST':
      return 'codigo-invalido';
    case 'CONFLICT':
      return 'codigo-usado';
    case 'FORBIDDEN':
    case 'UNAUTHENTICATED':
      return 'outra-conta';
    default:
      return 'servidor';
  }
}

interface Envelope {
  ok?: boolean;
  data?: {
    member?: { id?: unknown; name?: unknown; role?: unknown; familyId?: unknown };
    family?: { id?: unknown; name?: unknown };
  };
  error?: { code?: string; message?: string };
}

function roleOf(value: unknown): FamilyRole {
  // Um papel desconhecido vindo do servidor nao pode virar permissoes a mais.
  // O menos privilegiado e a escolha segura — e a lista vem de `@flos/shared`,
  // para nao haver aqui uma segunda copia que envelhece sozinha.
  return (typeof value === 'string' && (ALL_ROLES as readonly string[]).includes(value)
    ? value
    : 'viewer') as FamilyRole;
}

/**
 * Troca o codigo por uma sessao real.
 *
 * Nunca lança: devolve sempre um resultado com um motivo em português. Quem
 * chama esta função está numa tela de instalação, e uma exceção ali é uma tela
 * branca no primeiro minuto de uso.
 */
export async function redeemInvite(options: RedeemInviteOptions): Promise<RedeemResult> {
  const code = normalizeInviteCode(options.code);
  if (!isWellFormedInviteCode(code)) {
    return { ok: false, reason: 'codigo-invalido', message: REDEEM_MESSAGES['codigo-invalido'] };
  }

  const url = options.url.trim();
  if (!url) {
    return { ok: false, reason: 'sem-servidor', message: REDEEM_MESSAGES['sem-servidor'] };
  }

  const fetchImpl = options.fetchImpl ?? (globalThis.fetch as typeof fetch | undefined);
  if (!fetchImpl) {
    return { ok: false, reason: 'sem-rede', message: REDEEM_MESSAGES['sem-rede'] };
  }

  let idToken: string | null = null;
  try {
    idToken = await options.getToken();
  } catch {
    idToken = null;
  }
  if (!idToken) {
    return { ok: false, reason: 'sem-conta', message: REDEEM_MESSAGES['sem-conta'] };
  }

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), options.timeoutMs ?? 30_000)
    : null;

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      // `text/plain` e obrigatorio: com `application/json` o navegador faz um
      // `OPTIONS` de verificacao previa a que o servidor nao sabe responder, e o
      // pedido morre. O corpo continua a ser JSON.
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        action: 'invite.redeem',
        idToken,
        code,
        ...(options.displayName?.trim() ? { name: options.displayName.trim() } : {}),
      }),
      redirect: 'follow',
      ...(controller ? { signal: controller.signal } : {}),
    });
  } catch {
    return { ok: false, reason: 'sem-rede', message: REDEEM_MESSAGES['sem-rede'] };
  } finally {
    if (timer) clearTimeout(timer);
  }

  let text: string;
  try {
    text = await response.text();
  } catch {
    return { ok: false, reason: 'servidor', message: REDEEM_MESSAGES.servidor };
  }

  // Antes de o router arrancar, a infraestrutura pode devolver uma PAGINA HTML.
  // Fazer `JSON.parse` as cegas rebentaria com "Unexpected token <" e esconderia
  // o erro verdadeiro.
  const head = text.trimStart().slice(0, 40).toLowerCase();
  if (head.startsWith('<!doctype html') || head.startsWith('<html')) {
    return { ok: false, reason: 'servidor', message: REDEEM_MESSAGES.servidor };
  }

  let envelope: Envelope;
  try {
    envelope = JSON.parse(text) as Envelope;
  } catch {
    return { ok: false, reason: 'servidor', message: REDEEM_MESSAGES.servidor };
  }

  if (envelope.ok === false || envelope.error) {
    const reason = failureFor(envelope.error?.code ?? '');
    // As mensagens do servidor ja vem em portugues e sabem mais do que nos
    // sobre o que aconteceu.
    const message = envelope.error?.message?.trim() || REDEEM_MESSAGES[reason];
    return { ok: false, reason, message };
  }

  const member = envelope.data?.member;
  const family = envelope.data?.family;
  const familyId =
    typeof family?.id === 'string' && family.id
      ? family.id
      : typeof member?.familyId === 'string'
        ? member.familyId
        : '';
  const memberId = typeof member?.id === 'string' ? member.id : '';

  // Sem familia e sem membro nao ha sessao para criar. Aceitar isto seria voltar
  // exatamente ao problema que este arquivo existe para resolver.
  if (!familyId || !memberId) {
    return { ok: false, reason: 'servidor', message: REDEEM_MESSAGES.servidor };
  }

  return {
    ok: true,
    familyId,
    memberId,
    displayName:
      typeof member?.name === 'string' && member.name.trim()
        ? member.name.trim()
        : options.displayName?.trim() || 'Eu',
    role: roleOf(member?.role),
    familyName: typeof family?.name === 'string' ? family.name : '',
  };
}
